// NodeUserDetails.tsx
import { Stack, Typography } from "@mui/material";
import { useMemo } from "react";
import { DetailsTree, DetailsTreeItemValue } from "@synota-io/synota-shared-ui";
import { INodeUser } from "../../../api/hooks/useNodeUsers";
import { buildTreeItems } from "../utils";

interface NodeUserDetailsProps {
  user: INodeUser | null;
}

export const NodeUserDetails = ({ user }: NodeUserDetailsProps) => {
  const userDetailsChildren: DetailsTreeItemValue[] = useMemo(() => {
    if (!user) return [];

    const userNodeId = `user_${user.email}`;
    return [
      {
        id: `${userNodeId}_email`,
        label: "Email",
        value: user.email,
      },
      user.public_name && {
        id: `${userNodeId}_public_name`,
        label: "Public Name",
        value: user.public_name,
      },
      {
        id: `${userNodeId}_role`,
        label: "Role",
        value: user.role,
      },
      user.admin_user_email && {
        id: `${userNodeId}_admin_email`,
        label: "Admin Email",
        value: user.admin_user_email,
      },
      user.payment_method_types.length > 0 && {
        id: `${userNodeId}_payment_method`,
        label: "Payment Method",
        value: user.payment_method_types.join(", "),
      },
      user.num_active_contracts && {
        id: `${userNodeId}_contracts_active`,
        label: `Active Contracts`,
        value: user.num_active_contracts.toString(),
      },
      user.num_ended_contracts && {
        id: `${userNodeId}_contracts_ended`,
        label: `Ended Contracts`,
        value: user.num_ended_contracts.toString(),
      },
      user.num_future_contracts && {
        id: `${userNodeId}_contracts_future`,
        label: `Future Contracts`,
        value: user.num_future_contracts.toString(),
      },
      user.num_pending_contracts && {
        id: `${userNodeId}_contracts_pending`,
        label: `Pending Contracts`,
        value: user.num_pending_contracts.toString(),
      },
      user.is_consumer && {
        id: `${userNodeId}_is_consumer`,
        label: "Consumer",
        value: user.is_consumer ? "Yes" : "No",
      },
      user.is_supplier && {
        id: `${userNodeId}_is_supplier`,
        label: "Supplier",
        value: user.is_supplier ? "Yes" : "No",
      },
      user.currency_converter_configurations.length > 0 && {
        id: `${userNodeId}_currency_converter`,
        label: "Currency Converter",
        children: buildTreeItems(user.currency_converter_configurations, `${userNodeId}_currency`),
      },
      user.disbursement_configuration && {
        id: `${userNodeId}_disbursement_config`,
        label: "Disbursement Configuration",
        children: buildTreeItems(user.disbursement_configuration, `${userNodeId}_disbursement`),
      },
    ].filter(Boolean) as DetailsTreeItemValue[];
  }, [user]);

  return user ? (
    <DetailsTree
      disablePaper
      defaultExpandedItems={[`user_${user.email}`]}
      items={userDetailsChildren}
    />
  ) : (
    <Stack gap={4} pt={4} display="flex" justifyContent="center" alignItems="center">
      <Typography textAlign="center" py={5}>
        No user details available.
      </Typography>
    </Stack>
  );
};
